import { supabase } from "./supabaseClient";
import { randomId } from "./randomId";

// Cover images. Collection covers are PRIVATE (stored in the existing
// thought-images bucket, read via signed URLs). Edition covers are PUBLIC
// (editions are public share links) and live in the edition-covers bucket.
// Publishing an edition can inherit the collection's private cover by copying
// it into the public bucket — see promoteCollectionCoverToPublic.

const PRIVATE_BUCKET = "thought-images";
const PUBLIC_BUCKET = "edition-covers";

// Raster images only. SVG is deliberately excluded — it's active content, and
// collection/trail covers can be promoted into the PUBLIC edition-covers bucket.
const ALLOWED_COVER_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_COVER_BYTES = 5 * 1024 * 1024;

function assertSafeCover(file: File): void {
  if (!ALLOWED_COVER_TYPES.includes(file.type)) {
    throw new Error("Cover must be a JPG, PNG, GIF, or WebP image.");
  }
  if (file.size > MAX_COVER_BYTES) {
    throw new Error("Cover image must be under 5 MB.");
  }
}

function safeExt(name: string): string {
  const ext = (name.split(".").pop() || "jpg").toLowerCase();
  return /^[a-z0-9]+$/.test(ext) ? ext : "jpg";
}

/** Upload a private collection cover. Returns the storage path (read via signed URL). */
export async function uploadCollectionCover(
  file: File,
  userId: string,
  collectionId: string
): Promise<string> {
  assertSafeCover(file);
  const path = `${userId}/collections/${collectionId}/${randomId()}.${safeExt(file.name)}`;
  const { error } = await supabase.storage
    .from(PRIVATE_BUCKET)
    .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
  if (error) throw error;
  return path;
}

/** Upload a private trail cover. Returns the storage path (read via signed URL). */
export async function uploadTrailCover(
  file: File,
  userId: string,
  threadId: string
): Promise<string> {
  assertSafeCover(file);
  const path = `${userId}/trails/${threadId}/${randomId()}.${safeExt(file.name)}`;
  const { error } = await supabase.storage
    .from(PRIVATE_BUCKET)
    .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
  if (error) throw error;
  return path;
}

/** Upload a public edition cover. Returns the public URL. */
export async function uploadEditionCover(file: File, userId: string): Promise<string> {
  assertSafeCover(file);
  const path = `${userId}/${randomId()}.${safeExt(file.name)}`;
  const { error } = await supabase.storage
    .from(PUBLIC_BUCKET)
    .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
  if (error) throw error;
  return supabase.storage.from(PUBLIC_BUCKET).getPublicUrl(path).data.publicUrl;
}

/**
 * Copy a private collection cover into the public edition-covers bucket so a
 * published edition can show it. Returns the new public URL, or null on failure.
 */
export async function promoteCollectionCoverToPublic(
  coverPath: string,
  userId: string
): Promise<string | null> {
  const { data: signed, error: signErr } = await supabase.storage
    .from(PRIVATE_BUCKET)
    .createSignedUrl(coverPath, 60);
  if (signErr || !signed?.signedUrl) return null;
  const res = await fetch(signed.signedUrl);
  if (!res.ok) return null;
  const blob = await res.blob();
  const path = `${userId}/${randomId()}.${safeExt(coverPath)}`;
  const { error: upErr } = await supabase.storage
    .from(PUBLIC_BUCKET)
    .upload(path, blob, { contentType: blob.type || "application/octet-stream", upsert: false });
  if (upErr) return null;
  return supabase.storage.from(PUBLIC_BUCKET).getPublicUrl(path).data.publicUrl;
}
